import { forwardRef, memo, useMemo } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import { Typography } from "@components/atoms/typography";
import { mergeProps } from "@/utils/mergeProps";
import { VButtonVariants } from "@components/atoms/button/variant";
import { ButtonLoader } from "@components/atoms/loader";
import { generateButtonClassname } from "@components/atoms/button/helper";
import type { IButtonProps } from "@components/atoms/button/types";

/**
 * Base Button component.
 * @param {IButtonProps} outProps - The props for the Button component.
 * @param {React.Ref<HTMLButtonElement>} ref The ref for the Button component.
 * @returns {React.ReactElement} The rendered Button component.
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, IButtonProps>((outProps, ref) => {
    const { t } = useTranslation("actions");

    const modeProps = useMemo(() => {
      return VButtonVariants(t)[outProps?.mode];
    }, [outProps?.mode, t]);
    const mergedProps = useMemo(
      () => mergeProps(outProps, modeProps),
      [modeProps, outProps],
    );

    const {
      type,
      color = "primary",
      icon,
      iconDir,
      rounded,
      name,
      className,
      disabled,
      isLoading,
      label,
      size = "normal",
      variant = "contained",
      rootAttributes,
      onClick,
      onSubmit,
      children,
      linkTo,
    } = useMemo(() => mergedProps, [mergedProps]);

    // Generate class names based on size and rounded props
    const generatedClass = useMemo(() => {
      return generateButtonClassname(size, rounded);
    }, [rounded, size]);

    const navigate = useNavigate();

    const _onClick = useMemo(
      () =>
        linkTo
          ? () => {
              navigate(linkTo);
            }
          : onClick,
      [linkTo, navigate, onClick],
    );

    return (
      <button
        data-size={size}
        data-color={color}
        data-variant={variant}
        disabled={disabled || isLoading}
        className={clsx(
          generatedClass,
          classes.button,
          (disabled || isLoading) && classes.buttonDisabled,
          className,
        )}
        onClick={_onClick}
        onSubmit={onSubmit}
        {...{
          ref,
          /* eslint-disable-next-line react/button-has-type */
          type: outProps.type || type || "button",
          name,
          ...rootAttributes,
        }}
      >
        {isLoading ? (
          // TODO: Add All variants based loader color
          <ButtonLoader />
        ) : (
          <div
            className={clsx(classes.button__content)}
            data-icon-dir={iconDir}
          >
            {icon || ""}
            <Typography color={"inherit"} createSpan>
              {label}
              {children}
            </Typography>
          </div>
        )}
      </button>
    );
  }),
);

// Set display name for the Button component
Button.displayName = "Button";
